import { FLOW_CONTEXT_VERSION } from "./contracts.js";
import { buildFlowSummaryFromContext } from "./summary.js";

export const SUBPROCESS_TIMING_EXPAND = "expand";
export const SUBPROCESS_TIMING_FIXED = "fixed_duration";

export function normalizeSubprocessTimingMode({ explicitTimingMode, subprocessId, subprocesses, rawModulesNonEmpty, modulesByName }) {
    if (explicitTimingMode === SUBPROCESS_TIMING_EXPAND || explicitTimingMode === SUBPROCESS_TIMING_FIXED) {
        return explicitTimingMode;
    }
    // 移行ルール: 未指定の場合
    const definition = (subprocesses || []).find(s => String(s.id) === String(subprocessId));
    if (definition && definition.moduleName) {
        const hasNodes = rawModulesNonEmpty 
            ? !!rawModulesNonEmpty.get(definition.moduleName)
            : !!modulesByName?.get(definition.moduleName)?.flowNodes?.length;
        if (hasNodes) {
            return SUBPROCESS_TIMING_EXPAND;
        }
    }
    return SUBPROCESS_TIMING_FIXED;
}

function normalizeConditionEntry(entry = {}, index = 0, fallbackName = "条件") {
    const baseLabel = index === 0 ? fallbackName : `条件${index + 1}`;
    const label = String(entry?.label || baseLabel).trim() || baseLabel;
    const expression = String(entry?.expression || label).trim() || label;
    return { label, expression };
}

function normalizeConditionEntries(entries = [], fallbackName = "条件", mode = "single") {
    const source = Array.isArray(entries) ? entries : [];
    const normalized = source
        .map((entry, index) => normalizeConditionEntry(entry, index, fallbackName))
        .filter((entry) => entry.label || entry.expression);
    if (!normalized.length) {
        normalized.push(normalizeConditionEntry({ label: fallbackName, expression: fallbackName }, 0, fallbackName));
    }
    return mode === "composite" ? normalized : [normalized[0]];
}

export function extractFlowNodesFromSnapshot(flowData = {}, helpers = {}) {
    const getNodeData = helpers.getNodeData || ((node) => node?.data || {});
    const getNodeTypeFromData = helpers.getNodeTypeFromData || ((data, node) => data?.nodeType || data?.type || node?.name || "");
    const resolveMotionByNode = helpers.resolveMotionByNode || (() => null);
    return Object.values(flowData).map(node => {
        const data = getNodeData(node);
        const foundMotion = resolveMotionByNode(node, data);
        const nodeType = getNodeTypeFromData(data, node);
        return {
            id: String(node.id),
            type: nodeType,
            name: data.label || data.name || data.conditionText || data.errorCode || foundMotion?.motion?.name || "",
            label: data.label || "",
            ...(nodeType === "subprocess" ? {
                subprocessId: data.subprocessId || "",
                subprocessName: data.subprocessName || data.label || "",
                durationSec: Number.isFinite(Number(data.durationSec)) ? Number(data.durationSec) : 1,
                timingMode: normalizeSubprocessTimingMode({
                    explicitTimingMode: data.timingMode,
                    subprocessId: data.subprocessId || "",
                    subprocesses: typeof helpers.getSubprocesses === "function" ? helpers.getSubprocesses() : [],
                    rawModulesNonEmpty: typeof helpers.getRawModulesNonEmpty === "function" ? helpers.getRawModulesNonEmpty() : null,
                    modulesByName: typeof helpers.getModules === "function"
                        ? new Map((helpers.getModules() || []).map(m => [m.moduleName, m]))
                        : null
                })
            } : {}),
            ...(nodeType === "motion" ? {
                axisId: data.axisId || "",
                motionId: data.motionId || "",
                axisName: foundMotion?.axis?.name || "",
                motionName: foundMotion?.motion?.name || data.motionName || "",
                actuatorId: data.actuatorId || "",
                actionId: data.actionId || ""
            } : {}),
            ...(nodeType === "condition" ? {
                conditionText: String(data.conditionText || "条件").trim() || "条件",
                conditionMode: data.conditionMode === "composite" ? "composite" : "single",
                operator: data.operator === "OR" ? "OR" : "AND",
                conditions: normalizeConditionEntries(
                    data.conditions,
                    String(data.conditionText || "条件").trim() || "条件",
                    data.conditionMode === "composite" ? "composite" : "single"
                ),
                ...(data.conditionKind ? {
                    conditionKind: data.conditionKind,
                    conditionName: data.conditionName || "",
                    conditionValue: data.conditionValue === false ? false : true,
                    conditionActuatorId: data.conditionActuatorId || "",
                    conditionSignalId: data.conditionSignalId || ""
                } : {}),
                trueLabel: data.trueLabel || "ON",
                falseLabel: data.falseLabel || "OFF",
                timeoutLabel: data.timeoutLabel || "TIMEOUT",
                timeoutSec: Number.isFinite(Number(data.timeoutSec)) ? Number(data.timeoutSec) : 0
            } : {}),
            ...(nodeType === "button" ? {
                signalName: data.signalName || "",
                mode: data.mode === "alternate" ? "alternate" : "momentary",
                trueLabel: data.trueLabel || "ON",
                falseLabel: data.falseLabel || "OFF"
            } : {}),
            ...(nodeType === "signal_output" ? {
                signalScope: data.signalScope === "internal_flag" ? "internal_flag" : "external",
                signalName: data.signalName || "",
                action: data.action || "ON"
            } : {}),
            ...(nodeType === "wait" ? {
                label: data.label || "待機",
                durationSec: Number.isFinite(Number(data.durationSec)) ? Number(data.durationSec) : 1
            } : {}),
            ...(nodeType === "error" ? {
                errorCode: data.errorCode || "E001",
                errorMessage: data.errorMessage || "エラー"
            } : {}),
            ...(nodeType === "start" ? {
                label: data.label || "開始"
            } : {}),
            ...(nodeType === "end" ? {
                label: data.label || "終了",
                result: data.result || "normal"
            } : {}),
            inputs: Object.keys(node.inputs || {}),
            outputs: Object.keys(node.outputs || {})
        };
    });
}

export function extractFlowConnectionsFromSnapshot(flowData = {}) {
    const connections = [];
    for (const node of Object.values(flowData)) {
        const outputs = node?.outputs || {};
        for (const [outputName, output] of Object.entries(outputs)) {
            for (const connection of output?.connections || []) {
                connections.push({
                    from: String(node.id),
                    to: String(connection?.node ?? ""),
                    output: String(outputName || "output_1"),
                    input: String(connection?.output || connection?.input || "input_1")
                });
            }
        }
    }
    return connections.filter((item) => item.from && item.to);
}

export function extractFlowAxesFromProject(project = {}, helpers = {}) {
    const axes = Array.isArray(project.axes) ? project.axes : [];
    const getAxisDistanceUnit = helpers.getAxisDistanceUnit || (() => "-");
    const getAxisAccelUnit = helpers.getAxisAccelUnit || (() => "-");
    const getAxisSpeedUnit = helpers.getAxisSpeedUnit || (() => "-");
    const findMotion = helpers.findMotion || (() => null);
    return axes.map(axis => ({
        id: axis.id,
        name: axis.name,
        motionType: axis.motionType,
        axisMaxSpeed: axis.axisMaxSpeed,
        units: {
            distance: getAxisDistanceUnit(axis),
            accel: getAxisAccelUnit(axis),
            speed: getAxisSpeedUnit(axis)
        },
        motions: (axis.motions || []).map(motion => ({
            id: motion.id,
            name: motion.name,
            distance: motion.distance,
            accel: motion.accel,
            decel: motion.decel,
            maxSpeed: motion.maxSpeed,
            waitTime: motion.waitTime,
            absoluteStartSec: motion.absoluteStartSec,
            dependency: motion.dependency ? {
                sourceMotionId: motion.dependency.sourceMotionId,
                sourceMotionName: findMotion(motion.dependency.sourceMotionId)?.motion?.name || "",
                sourceAxisName: findMotion(motion.dependency.sourceMotionId)?.axis?.name || "",
                offsetSec: motion.dependency.offsetSec
            } : null,
            startConditionText: motion.startConditionText || ""
        }))
    }));
}

export function extractFlowConditionsFromSnapshot(flowData = {}, helpers = {}) {
    const getNodeData = helpers.getNodeData || ((node) => node?.data || {});
    const getNodeTypeFromData = helpers.getNodeTypeFromData || ((data, node) => data?.nodeType || data?.type || node?.name || "");
    return Object.values(flowData)
        .filter(node => getNodeTypeFromData(getNodeData(node), node) === "condition")
        .map(node => {
            const data = getNodeData(node);
            const conditionText = String(data.conditionText || "条件").trim() || "条件";
            const conditionMode = data.conditionMode === "composite" ? "composite" : "single";
            return {
                conditionText,
                conditionMode,
                operator: data.operator === "OR" ? "OR" : "AND",
                conditions: normalizeConditionEntries(data.conditions, conditionText, conditionMode),
                trueLabel: data.trueLabel || "ON",
                falseLabel: data.falseLabel || "OFF",
                timeoutLabel: data.timeoutLabel || "TIMEOUT",
                timeoutSec: data.timeoutSec ?? 0
            };
        });
}

export function extractFlowErrorsFromSnapshot(flowData = {}, helpers = {}) {
    const getNodeData = helpers.getNodeData || ((node) => node?.data || {});
    const getNodeTypeFromData = helpers.getNodeTypeFromData || ((data, node) => data?.nodeType || data?.type || node?.name || "");
    return Object.values(flowData)
        .filter(node => getNodeTypeFromData(getNodeData(node), node) === "error")
        .map(node => {
            const data = getNodeData(node);
            return {
                errorCode: data.errorCode || "E001",
                errorMessage: data.errorMessage || "エラー"
            };
        });
}

export function buildFlowContextFromSources({
    project = {},
    flowData = {},
    dialect = "generic-st",
    assistantPipeline = [],
    plcProfile = null
} = {}, helpers = {}) {
    const activeProfile = plcProfile && typeof plcProfile === "object"
        ? plcProfile
        : {
            id: dialect,
            name: dialect,
            promptRules: "",
            vendorTarget: dialect
        };
    const rawModulesNonEmpty = new Map();
    const rawDrawflow = project?.flow?.drawflowData?.drawflow || {};
    for (const [moduleName, moduleObj] of Object.entries(rawDrawflow)) {
        if (moduleObj && moduleObj.data) {
            const hasNodes = Object.keys(moduleObj.data).length > 0;
            rawModulesNonEmpty.set(moduleName, hasNodes);
        }
    }

    const subprocesses = Array.isArray(project?.flow?.subprocesses)
        ? project.flow.subprocesses.map((item) => ({
              id: item.id,
              name: item.name || "サブプロセス",
              moduleName: item.moduleName || "",
              durationSec: Number.isFinite(Number(item.durationSec)) ? Math.max(0, Number(item.durationSec)) : 1
          }))
        : [];

    const extendedHelpers = {
        ...helpers,
        getSubprocesses: () => subprocesses,
        getRawModulesNonEmpty: () => rawModulesNonEmpty
    };

    const flowNodes = extractFlowNodesFromSnapshot(flowData, extendedHelpers);
    const connections = extractFlowConnectionsFromSnapshot(flowData);
    const axes = extractFlowAxesFromProject(project, extendedHelpers);
    const conditions = extractFlowConditionsFromSnapshot(flowData, extendedHelpers);
    const errors = extractFlowErrorsFromSnapshot(flowData, extendedHelpers);
    const internalFlags = Array.isArray(project?.flow?.internalFlags)
        ? project.flow.internalFlags
            .map((item) => ({ name: String(item?.name || "").trim() }))
            .filter((item) => item.name)
        : [];

    const modules = [];
    for (const [moduleName, moduleObj] of Object.entries(rawDrawflow)) {
        if (moduleObj && moduleObj.data) {
            const mNodes = extractFlowNodesFromSnapshot(moduleObj.data, extendedHelpers);
            const mConns = extractFlowConnectionsFromSnapshot(moduleObj.data);
            modules.push({
                moduleName,
                flowNodes: mNodes,
                connections: mConns
            });
        }
    }

    const context = {
        contractVersion: FLOW_CONTEXT_VERSION,
        projectVersion: project.version,
        vendorTarget: activeProfile.vendorTarget || dialect,
        dialect: activeProfile.vendorTarget || dialect,
        plcProfileId: activeProfile.id || dialect,
        plcProfileName: activeProfile.name || dialect,
        plcProfilePromptRules: activeProfile.promptRules || "",
        units: { ...(project.units || {}) },
        axes,
        flowNodes,
        connections,
        conditions,
        errors,
        subprocesses,
        internalFlags,
        modules,
        actuators: Array.isArray(project.actuators) ? project.actuators : [],
        signals: Array.isArray(project.signals) ? project.signals : [],
        assistantPipeline: assistantPipeline.length ? [...assistantPipeline] : [
            "flow-analysis",
            "neutral-ir",
            "vendor-conversion",
            "validation"
        ]
    };
    context.summary = buildFlowSummaryFromContext(context);
    return context;
}
